import React, { createContext, useEffect, useMemo, useState } from 'react'
import { VerticalSlug, VerticalUIContextValue } from './types'
import { getVerticalProfile, GENERIC_VERTICAL_PROFILE } from './registry'
import { normalizeToVertical } from './normalizeVertical'
import { useAuthStore } from '@/store/authStore'
import api from '@/services/api'

export const VerticalUIContext = createContext<VerticalUIContextValue>({
  slug: 'otro',
  profile: GENERIC_VERTICAL_PROFILE,
  loading: false,
})

// In-memory session cache to guarantee at most 1 fetch per tenantId per session
const fetchedTenantsCache = new Set<string>()

export interface VerticalUIProviderProps {
  children: React.ReactNode
  forcedSlug?: VerticalSlug
  forcedBusinessType?: string | null
}

export async function fetchTenantBusinessTypeOnce(tenantId: string): Promise<string | null> {
  if (fetchedTenantsCache.has(tenantId)) {
    const currentTenant =
      typeof (useAuthStore as any)?.getState === 'function'
        ? (useAuthStore as any).getState()?.tenant
        : null
    return currentTenant?.businessType || null
  }
  fetchedTenantsCache.add(tenantId)

  try {
    const res = await api.get('/tenants/current')
    const data = res?.data
    const resolvedType =
      data?.businessType ||
      data?.tenantProfile?.businessType ||
      null

    if (typeof (useAuthStore as any)?.setState === 'function') {
      ;(useAuthStore as any).setState((prev: any) => {
        if (!prev?.tenant) return prev
        return {
          tenant: {
            ...prev.tenant,
            businessType: resolvedType,
          },
        }
      })
    }
    return resolvedType
  } catch (err) {
    console.warn('[VerticalUIProvider] Could not load tenant businessType, defaulting to generic profile', err)
    if (typeof (useAuthStore as any)?.setState === 'function') {
      ;(useAuthStore as any).setState((prev: any) => {
        if (!prev?.tenant) return prev
        return {
          tenant: {
            ...prev.tenant,
            businessType: null,
          },
        }
      })
    }
    return null
  }
}

export const VerticalUIProvider: React.FC<VerticalUIProviderProps> = ({
  children,
  forcedSlug,
  forcedBusinessType,
}) => {
  const storeTenant =
    typeof useAuthStore === 'function' ? useAuthStore((s: any) => s?.tenant) : null
  const stateTenant =
    typeof (useAuthStore as any)?.getState === 'function'
      ? (useAuthStore as any).getState()?.tenant
      : null
  const tenant = storeTenant || stateTenant || null
  const tenantId = tenant?.id || null
  const storedBusinessType = tenant?.businessType

  const [loading, setLoading] = useState<boolean>(() => {
    if (forcedSlug || forcedBusinessType !== undefined) return false
    if (!tenantId) return false
    // Only loading if tenantId exists, businessType is undefined, and hasn't been fetched yet
    return storedBusinessType === undefined && !fetchedTenantsCache.has(tenantId)
  })

  // Determine active slug
  const activeSlug: VerticalSlug = useMemo(() => {
    if (forcedSlug) return forcedSlug
    if (forcedBusinessType !== undefined) return normalizeToVertical(forcedBusinessType)
    return normalizeToVertical(storedBusinessType)
  }, [forcedSlug, forcedBusinessType, storedBusinessType])

  const profile = useMemo(() => getVerticalProfile(activeSlug), [activeSlug])

  useEffect(() => {
    if (forcedSlug || forcedBusinessType !== undefined) {
      setLoading(false)
      return
    }

    if (!tenantId) {
      setLoading(false)
      return
    }

    // If businessType is already defined in store or already fetched in this session, skip fetch
    if (storedBusinessType !== undefined || fetchedTenantsCache.has(tenantId)) {
      setLoading(false)
      return
    }

    let isMounted = true
    setLoading(true)

    fetchTenantBusinessTypeOnce(tenantId)
      .finally(() => {
        if (isMounted) {
          setLoading(false)
        }
      })

    return () => {
      isMounted = false
    }
  }, [tenantId, storedBusinessType, forcedSlug, forcedBusinessType])

  const value = useMemo<VerticalUIContextValue>(
    () => ({
      slug: activeSlug,
      profile,
      loading,
    }),
    [activeSlug, profile, loading]
  )

  return <VerticalUIContext.Provider value={value}>{children}</VerticalUIContext.Provider>
}

/** Helper to clear session cache in tests */
export function _resetVerticalUiSessionCache(): void {
  fetchedTenantsCache.clear()
}
